import { mockResponse, request, isMockMode } from './client.js';
import { VIRTUAL_PRODUCTS } from '../data/virtual-products.js';
import { isVirtualExpired } from '../utils/virtualAvailability.js';

/**
 * 虛擬商品 API（會員制：需登入）。後端在 feat/virtual-product。
 * 後端跑起來後在 .env 設 REACT_APP_USE_MOCK_VIRTUAL=false 即接真實後端。
 *
 * VirtualProductDto：{ id, slug, name, machine, category, validity,
 *   tags[], price, description, thumbUrl, images[], previewUrl, previewUrls[], status,
 *   variants:[{ id, name, price }] }
 *
 * images 為圖片網址陣列（第一張 = 列表縮圖）；舊資料只有 thumbUrl 時
 * 後端會自動把 thumbUrl 當第一張圖回傳。
 * previewUrls 為預覽影片網址陣列（第一支 = 主預覽）；舊資料只有 previewUrl 時
 * 後端同樣會把它當第一支影片回傳。
 *
 * 前台用 slug 做網址 / 詢問單快照；後台 CRUD 用數字 id。
 */

const clone = (x) => JSON.parse(JSON.stringify(x));
// mock 資料用 id 當 slug；正規化補上 slug 讓前端統一用 slug
const norm = (p) => ({ ...clone(p), slug: p.slug ?? p.id, expired: isVirtualExpired(p) });

// 後台 mock 可變庫存
let _store = VIRTUAL_PRODUCTS.map(norm);

const isLegacyEndedDraft = (p) => (p.status ?? 'active') === 'draft' && isVirtualExpired(p);
const isPubliclyViewable = (p) => (p.status ?? 'active') !== 'draft' || isLegacyEndedDraft(p);
function sortInPlace(arr, sort) {
  const [sf, sd] = (sort || '').split(',');
  if (!sf) return arr;
  return arr.sort((a, b) => {
    const r = String(a[sf] ?? '').localeCompare(String(b[sf] ?? ''), undefined, { numeric: true });
    return sd === 'desc' ? -r : r;
  });
}

function paginate(rows, page, limit) {
  const start = (page - 1) * limit;
  return { items: rows.slice(start, start + limit).map(clone), total: rows.length, page, limit, totalPages: Math.ceil(rows.length / limit) };
}

// ── 前台 ──────────────────────────────────────────────

export function getVirtualProducts({ machine, q, availability = 'active', page = 1, limit = 8, sort } = {}) {
  if (!isMockMode('virtual')) {
    const params = new URLSearchParams();
    if (machine && machine !== 'all') params.set('machine', machine);
    if (q) params.set('q', q);
    if (sort) params.set('sort', sort);
    params.set('availability', availability);
    params.set('page', page);
    params.set('limit', limit);
    return request(`/api/virtual-products?${params.toString()}`);
  }
  return mockResponse(() => {
    let rows = _store.filter(isPubliclyViewable).map(norm);
    if (availability === 'active') rows = rows.filter(p => !p.expired);
    if (availability === 'ended') rows = rows.filter(p => p.expired);
    if (machine && machine !== 'all') rows = rows.filter(p => p.machine === machine);
    if (q) {
      const kw = q.toLowerCase();
      rows = rows.filter(p => (p.name + p.slug).toLowerCase().includes(kw));
    }
    sortInPlace(rows, sort);
    return paginate(rows, page, limit);
  });
}

export function getVirtualProduct(slug) {
  if (!isMockMode('virtual')) return request(`/api/virtual-products/${slug}`);
  return mockResponse(() => {
    const p = _store.find(x => (x.slug ?? x.id) === slug);
    return p && isPubliclyViewable(p) ? norm(p) : null;
  });
}

// 分享連結（免登入）：用 token 取單一商品
export function getSharedVirtualProduct(token) {
  if (!isMockMode('virtual')) return request(`/api/virtual-products/shared/${token}`);
  return mockResponse(() => {
    const p = _store.find(x => x.shareToken === token);
    return p && isPubliclyViewable(p) ? norm(p) : null;
  });
}

// ── 後台 ──────────────────────────────────────────────

export function adminGetVirtuals({ machine, q, status, sort, page = 1, limit = 10 } = {}) {
  if (!isMockMode('virtual')) {
    const params = new URLSearchParams();
    if (machine && machine !== 'all') params.set('machine', machine);
    if (q) params.set('q', q);
    if (status) params.set('status', status);
    if (sort) params.set('sort', sort);
    params.set('page', page);
    params.set('limit', limit);
    return request(`/api/admin/virtual-products?${params.toString()}`);
  }
  return mockResponse(() => {
    let rows = _store.map(norm);
    if (machine && machine !== 'all') rows = rows.filter(p => p.machine === machine);
    if (q) {
      const kw = q.toLowerCase();
      rows = rows.filter(p => (p.name + p.slug).toLowerCase().includes(kw));
    }
    if (status) rows = rows.filter(p => (p.status ?? 'active') === status);
    sortInPlace(rows, sort);
    return paginate(rows, page, limit);
  });
}

export function adminGetVirtual(id) {
  if (!isMockMode('virtual')) return request(`/api/admin/virtual-products/${id}`);
  return mockResponse(() => {
    const p = _store.find(x => String(x.id) === String(id));
    return p ? norm(p) : null;
  });
}

export function adminCreateVirtual(data) {
  if (!isMockMode('virtual')) return request('/api/admin/virtual-products', { method: 'POST', body: data });
  return mockResponse(() => {
    const slug = (data.name || 'v').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-+|-+$)/g, '') || 'v';
    const item = { ...clone(data), id: slug, slug, status: data.status || 'draft', variants: data.variants || [] };
    _store = [item, ..._store];
    return norm(item);
  });
}

export function adminUpdateVirtual(id, data) {
  if (!isMockMode('virtual')) return request(`/api/admin/virtual-products/${id}`, { method: 'PATCH', body: data });
  return mockResponse(() => {
    const idx = _store.findIndex(x => String(x.id) === String(id));
    if (idx === -1) throw new Error(`找不到虛擬商品：${id}`);
    _store[idx] = { ..._store[idx], ...clone(data) };
    return norm(_store[idx]);
  });
}

export function adminDeleteVirtual(id) {
  if (!isMockMode('virtual')) return request(`/api/admin/virtual-products/${id}`, { method: 'DELETE' });
  return mockResponse(() => {
    _store = _store.filter(x => String(x.id) !== String(id));
    return null;
  });
}

// 產生 / 重新產生分享 token（回傳含 shareToken 的商品）
export function adminGenerateShareToken(id) {
  if (!isMockMode('virtual')) return request(`/api/admin/virtual-products/${id}/share-token`, { method: 'POST' });
  return mockResponse(() => {
    const idx = _store.findIndex(x => String(x.id) === String(id));
    if (idx === -1) throw new Error(`找不到虛擬商品：${id}`);
    _store[idx] = { ..._store[idx], shareToken: Math.random().toString(36).slice(2, 14) };
    return norm(_store[idx]);
  });
}

// 停用分享 token
export function adminRevokeShareToken(id) {
  if (!isMockMode('virtual')) return request(`/api/admin/virtual-products/${id}/share-token`, { method: 'DELETE' });
  return mockResponse(() => {
    const idx = _store.findIndex(x => String(x.id) === String(id));
    if (idx === -1) throw new Error(`找不到虛擬商品：${id}`);
    _store[idx] = { ..._store[idx], shareToken: null };
    return norm(_store[idx]);
  });
}
